import React, {
  useState, useEffect, useMemo, useCallback,
} from "react";
import {
  Modal, Table, Input, Button, Tag,
} from "antd";
import { toast } from "react-toastify";
import { updateInvoiceData } from "../../../actions/invoice";
import "./InvoiceDataModal.css";

export default function InvoiceDataModal({ open, onCancel, data = [] }) {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorIndices, setErrorIndices] = useState(new Set());
  const [submittedRows, setSubmittedRows] = useState(new Set());
  const [submittingRows, setSubmittingRows] = useState(new Set());

  const initializeTableData = (inputData) => {
    if (inputData && inputData.length > 0) {
      // Initialize table data with item_code as empty string if not provided
      const initializedData = inputData.map((item, index) => ({
        key: index,
        ...item,
      }));
      setTableData(initializedData);
      setErrorIndices(new Set()); // Clear errors when data changes
      setSubmittedRows(new Set()); // Clear submitted rows when data changes
      setSubmittingRows(new Set()); // Clear submitting rows when data changes
    }
  };

  useEffect(() => {
    initializeTableData(data);
  }, [data]);

  /**
   * Handle item code input change
   * @param {number} index - The row index
   * @param {string} value - The new item code value
   */
  const handleItemCodeChange = useCallback((index, value) => {
    const newData = [...tableData];
    // Update item_code to match dataIndex
    newData[index].item_code = value;
    setTableData(newData);

    // Clear error state for this input when user starts typing
    if (errorIndices.has(index)) {
      const newErrorIndices = new Set(errorIndices);
      newErrorIndices.delete(index);
      setErrorIndices(newErrorIndices);
    }
  }, [tableData, errorIndices]);

  /**
   * Check if an item code is valid
   * @param {string} itemCode - The item code to validate
   * @returns {boolean} - True if the item code is valid
   */
  const isValidItemCode = (itemCode) => {
    if (!itemCode || itemCode.trim() === "") {
      return false;
    }
    // Check if lowercase value equals "unknown"
    if (itemCode.toLowerCase().trim() === "unknown") {
      return false;
    }
    return true;
  };

  /**
   * Validate that all item codes are filled and set error states
   * Only validates non-submitted rows
   * @returns {boolean} - True if all item codes are valid
   */
  const validateItemCodes = () => {
    const invalidIndices = new Set();
    tableData.forEach((item, index) => {
      // Skip validation for already submitted rows
      if (submittedRows.has(index)) {
        return;
      }
      // Check item_code field
      const itemCode = item.item_code;
      if (!isValidItemCode(itemCode)) {
        invalidIndices.add(index);
      }
    });

    if (invalidIndices.size > 0) {
      setErrorIndices(invalidIndices);
      toast.error("Please fill in all Item Codes with valid values");
      return false;
    }

    setErrorIndices(new Set());
    return true;
  };

  /**
   * Handle submit button click for a single row
   */
  const handleRowSubmit = useCallback(async (index) => {
    const item = tableData[index];
    const itemCode = item.item_code;

    // Validate item code for this row
    if (!isValidItemCode(itemCode)) {
      setErrorIndices((prev) => {
        const newErrorIndices = new Set(prev);
        newErrorIndices.add(index);
        return newErrorIndices;
      });
      toast.error("Please enter a valid Item Code");
      return;
    }

    // Clear error for this row
    setErrorIndices((prev) => {
      if (prev.has(index)) {
        const newErrorIndices = new Set(prev);
        newErrorIndices.delete(index);
        return newErrorIndices;
      }
      return prev;
    });

    // Mark row as submitting using functional update
    setSubmittingRows((prev) => new Set([...prev, index]));

    // Prepare payload for single row update
    const payload = {
      updated_invoice_data: [{
        item_code: itemCode,
        qb_category_name: item.count_of_analysis,
        file_type: item.file_type,
      }],
    };

    try {
      const response = await updateInvoiceData(payload);
      if (response.success) {
        toast.success("Row updated successfully");
        // Mark row as submitted using functional update
        setSubmittedRows((prev) => new Set([...prev, index]));
      } else {
        toast.error(response.message || "Failed to update row");
      }
    } catch (error) {
      toast.error("An error occurred while updating the row");
    } finally {
      // Remove from submitting rows using functional update
      setSubmittingRows((prev) => {
        const newSubmittingRows = new Set(prev);
        newSubmittingRows.delete(index);
        return newSubmittingRows;
      });
    }
  }, [tableData]);

  /**
   * Handle submit button click (for all rows - keeping existing functionality)
   * Only submits non-submitted rows
   */
  // eslint-disable-next-line no-unused-vars
  const handleSubmit = async () => {
    if (!validateItemCodes()) {
      return;
    }

    // Filter out already submitted rows
    const nonSubmittedData = tableData.filter((item, index) => !submittedRows.has(index));

    if (nonSubmittedData.length === 0) {
      onCancel(true);
      return;
    }

    setLoading(true);
    try {
      // Prepare payload - only include non-submitted rows
      const payload = {
        updated_invoice_data: nonSubmittedData.map((item) => ({
          item_code: item.item_code,
          qb_category_name: item.count_of_analysis,
          file_type: item.file_type,
        })),
      };

      const response = await updateInvoiceData(payload);
      if (response.success) {
        toast.success(response.message);
        // Mark all non-submitted rows as submitted
        const allIndices = new Set();
        tableData.forEach((item, index) => {
          if (!submittedRows.has(index)) {
            allIndices.add(index);
          }
        });
        setSubmittedRows(new Set([...submittedRows, ...allIndices]));
        onCancel(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while updating invoice data");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: "Test",
      dataIndex: "count_of_analysis",
      key: "count_of_analysis",
    },
    {
      title: "Test Category",
      dataIndex: "test_category_list",
      key: "test_category_list",
      render: (text) => {
        if (!text || !Array.isArray(text) || text.length === 0) {
          return "-";
        }
        return (
          <div className="InvoiceDataModalTagsContainer separated">
            {text.map((item, idx) => (
              <Tag key={idx} className="InvoiceDataModalTag separated">
                {item}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "company_name_list",
      key: "company_name_list",
      render: (text) => {
        if (!text || !Array.isArray(text) || text.length === 0) {
          return "-";
        }
        return (
          <div className="InvoiceDataModalTagsContainer separated">
            {text.map((item, idx) => (
              <Tag key={idx} className="InvoiceDataModalTag separated">
                {item}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "File Type",
      dataIndex: "file_type",
      key: "file_type",
      width: 100,
      render: (text) => {
        let type = "-";
        if (text === 1) {
          type = "Regular";
        } else if (text === 2) {
          type = "IND";
        }
        return type;
      },
    },
    {
      title: "Item Code",
      dataIndex: "item_code",
      key: "item_code",
      width: 270,
      render: (text, record, index) => {
        const isSubmitted = submittedRows.has(index);
        const isSubmitting = submittingRows.has(index);

        return (
          <div className="InvoiceDataModalItemCodeContainer">
            <Input
              value={text}
              onChange={(e) => handleItemCodeChange(index, e.target.value)}
              placeholder="Enter item code"
              className="InvoiceDataModalItemCodeInput"
              status={errorIndices.has(index) ? "error" : ""}
              disabled={loading || isSubmitted || isSubmitting}
            />
            {!isSubmitted && (
              <Button
                className="InvoiceDataModalRowButton"
                onClick={() => handleRowSubmit(index)}
                loading={isSubmitting}
                disabled={loading || isSubmitting}
              >
                Submit
              </Button>
            )}
          </div>
        );
      },
    },
  ], [errorIndices, loading, handleItemCodeChange, submittedRows, submittingRows, handleRowSubmit]);

  return (
    <Modal
      centered
      title="Invoice Data - Unknown Item Code"
      closable={false}
      open={open}
      onCancel={() => onCancel(false)}
      maskClosable={false}
      keyboard={false}
      className="InvoiceDataModal"
      width="70vw"
      footer={[
        <Button key="cancel" onClick={() => onCancel(false)} disabled={loading}>
          Close
        </Button>,
        null,
        // <Button
        //   key="submit"
        //   type="primary"
        //   loading={loading}
        //   disabled={loading}
        //   onClick={handleSubmit}
        //   className="InvoiceDataModalSubmitButton"
        // >
        //   {submittedRows.size === tableData.length ? "Generate Data" : "Submit All"}
        // </Button>,
      ]}
    >
      <div className="InvoiceDataModalContent">
        <Table
          sticky
          dataSource={tableData}
          columns={columns}
          pagination={false}
          className="InvoiceDataModalTable"
          scroll={{ y: "75vh" }}
          rowClassName={(record, index) => (submittedRows.has(index) ? "InvoiceDataModalSubmittedRow" : "")}
        />
      </div>
    </Modal>
  );
}
